import { Box, Grid, Group, Overlay, Paper, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { DashboardHeader } from '../../components/dashboard-header/dashboard-header';
import { NewsCard } from '../../components/news-card/news-card';
import { OverlayedBarChart } from '../../components/overlayed-bar-chart/overlayed-bar-chart';
import { dashboardQueries } from '../../data-access/queries/dashboard.queries';

import { PageError } from './page.error';
import { PendingPage } from './page.pending';

import { formatDate, useQueriesWithGlobal } from '@data-access';
import { BarleyIcon, CanolaIcon, WheatIcon } from '@ui';

export function DashboardPage() {
  const { t } = useTranslation();
  const queries = useQueriesWithGlobal(dashboardQueries);
  const campaignStats = useQuery(queries.campaignStats());
  const news = useQuery(queries.news());

  const wheatStats =
    campaignStats.data?.wheat.stats.map((item) => ({
      name: formatDate({
        date: item.name,
        format: 'display',
      }),
      [t('dashboard.regionalAverage')]: item.mean_region,
      [t('dashboard.farmAverage')]: item.mean_farm,
    })) ?? [];

  const barleyStats =
    campaignStats.data?.barley.stats.map((item) => ({
      name: formatDate({
        date: item.name,
        format: 'display',
      }),
      [t('dashboard.farmAverage')]: item.mean_farm,
      [t('dashboard.regionalAverage')]: item.mean_region,
    })) ?? [];

  const rapeseedStats =
    campaignStats.data?.rapeseed.stats.map((item) => ({
      name: t(`common.${item.name}`),
      [t('dashboard.farmAverageRapeseed')]: item.mean_farm,
      [t('dashboard.regionalAverageRapeseed')]: item.mean_region,
    })) ?? [];

  return (
    <Grid>
      <DashboardHeader />
      <Grid.Col order={{ base: 2, lg: 3 }} span={{ base: 12, lg: 8 }}>
        <Paper pos="relative">
          <Group grow>
            <OverlayedBarChart
              actualDataKey={t('dashboard.farmAverage')}
              baselineDataKey={t('dashboard.regionalAverage')}
              data={wheatStats}
              olympicLabel={t('dashboard.olympicAverage')}
              olympicValue={campaignStats.data?.wheat.olympic_mean ?? 0}
              referenceLabel={t('dashboard.actualAverage')}
              referenceValue={campaignStats.data?.wheat.real_mean ?? 0}
              title={
                <Group align="center">
                  <WheatIcon size={40} />
                  <Text fw="bold">{t('dashboard.wheatYieldPotential')}</Text>
                </Group>
              }
            />
            <OverlayedBarChart
              actualDataColor="#CB9A3C"
              actualDataKey={t('dashboard.farmAverage')}
              baselineDataKey={t('dashboard.regionalAverage')}
              data={barleyStats}
              olympicLabel={t('dashboard.olympicAverage')}
              olympicValue={campaignStats.data?.barley.olympic_mean ?? 0}              
              referenceLabel={t('dashboard.actualAverage')}
              referenceValue={campaignStats.data?.barley.real_mean ?? 0}
              title={
                <Group align="center">
                  <BarleyIcon size={40} />
                  <Text fw="bold">{t('dashboard.barleyYieldPotential')}</Text>
                </Group>
              }
            />
          </Group>
        </Paper>
        <Paper
          mt="md"
          pos="relative"
          style={{
            overflow: 'visible',
          }}
          w="fit-content"
        >
          <Box miw={300}>
            <OverlayedBarChart
              actualDataColor="#8BC02B"
              actualDataKey={t('dashboard.farmAverageRapeseed')}
              baselineDataKey={t('dashboard.regionalAverageRapeseed')}
              data={rapeseedStats}
              title={
                <Group align="center">
                  <CanolaIcon size={40} />
                  <Text fw="bold">{t('dashboard.rapeBiomass')} </Text>
                </Group>
              }
            />
          </Box>
        </Paper>
      </Grid.Col>
      <Grid.Col order={4} span={{ base: 12, lg: 4 }}>
        <Paper>
          <Stack>
            {news.data?.map((item, index) => (
              <NewsCard key={index} {...item} />
            ))}
          </Stack>
        </Paper>
      </Grid.Col>
    </Grid>
  );
}

DashboardPage.Error = PageError;
DashboardPage.Pending = PendingPage;

export default DashboardPage;
